// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { Vote } from "le-coffre-resources/dist/SuperAdmin";
import type { IPostLiveVotesParams, LiveVote, IDeleteVotesParams, IGetLiveVotessparams } from "./LiveVotes";
import { buildLiveVotesCreatePayload, buildLiveVotesDeletePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface LiveVotesInstance {
	post(body: IPostLiveVotesParams): Promise<LiveVote>;
	delete(body: IDeleteVotesParams): Promise<Vote>;
	get(params: IGetLiveVotessparams): Promise<LiveVote[]>;
}

// Module-level singleton instance
let instance: LiveVotesInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

function createLiveVotes(): LiveVotesInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}

	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/live-votes");

	return {
		post: async (body: IPostLiveVotesParams): Promise<LiveVote> => {
			const url = new URL(baseUrl);
			try {
				const payload = buildLiveVotesCreatePayload(body);
				return await baseSuperAdmin.postRequest<LiveVote>(url, payload);
			} catch (err) {
				baseSuperAdmin.onError(err);
				return Promise.reject(err);
			}
		},
		delete: async (body: IDeleteVotesParams): Promise<Vote> => {
			const payload = buildLiveVotesDeletePayload(body.uid);
			const url = new URL(baseUrl.concat(`/${payload.uid}`));
			try {
				return await baseSuperAdmin.deleteRequest<Vote>(url);
			} catch (err) {
				baseSuperAdmin.onError(err);
				return Promise.reject(err);
			}
		},
		get: async (_params: IGetLiveVotessparams): Promise<LiveVote[]> => {
			const url = new URL(baseUrl);
			// Note: Query params would need to be handled if BaseApiService supports it
			try {
				return await baseSuperAdmin.getRequest<LiveVote[]>(url);
			} catch (err) {
				baseSuperAdmin.onError(err);
				return Promise.reject(err);
			}
		},
	};
}

export function getLiveVotesInstance(): LiveVotesInstance {
	if (!instance) {
		instance = createLiveVotes();
	}
	return instance;
}
